import { useState, useEffect, useCallback } from 'react';
import './Admin.css';

interface EquipmentItem {
  id: string;
  stockNumber: string;
  title: string;
  category: string;
  condition: string;
  specifications: Record<string, string>;
  description: string;
  image: string;
}

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [equipment, setEquipment] = useState<EquipmentItem[]>([]);
  const [editingItem, setEditingItem] = useState<EquipmentItem | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [uploadingImage, setUploadingImage] = useState(false);

  const getToken = () => sessionStorage.getItem('adminToken');
  const setToken = (token: string) => sessionStorage.setItem('adminToken', token);
  const clearToken = () => sessionStorage.removeItem('adminToken');

  const verifySession = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setIsAuthenticated(true);
      } else {
        clearToken();
      }
    } catch {
      clearToken();
    }
  }, []);

  const loadEquipment = useCallback(async () => {
    const token = getToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/admin/equipment`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setEquipment(data);
      }
    } catch (err) {
      console.error('Failed to load equipment:', err);
    }
  }, []);

  useEffect(() => {
    verifySession();
  }, [verifySession]);

  useEffect(() => {
    if (isAuthenticated) {
      loadEquipment();
    }
  }, [isAuthenticated, loadEquipment]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_BASE}/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });

      const data = await res.json();

      if (res.ok && data.token) {
        setToken(data.token);
        setIsAuthenticated(true);
        setPassword('');
      } else {
        setError(data.error || 'Login failed');
      }
    } catch {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    const token = getToken();
    if (token) {
      try {
        await fetch(`${API_BASE}/admin/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // Ignore errors
      }
    }
    clearToken();
    setIsAuthenticated(false);
    setEquipment([]);
  };

  const handleSave = async () => {
    const token = getToken();
    if (!token) return;

    setSaveStatus('saving');
    try {
      const res = await fetch(`${API_BASE}/admin/equipment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ equipment }),
      });

      if (res.ok) {
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 3000);
      } else {
        setSaveStatus('error');
      }
    } catch {
      setSaveStatus('error');
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !editingItem) return;

    const token = getToken();
    if (!token) return;

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      alert('Image too large. Maximum size is 10MB.');
      return;
    }

    setUploadingImage(true);

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data URL prefix to get just the base64 data
          const base64Data = result.split(',')[1];
          resolve(base64Data);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const res = await fetch(`${API_BASE}/admin/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          imageData: base64,
          filename: file.name,
          mimeType: file.type,
        }),
      });

      const data = await res.json();
      if (res.ok && data.filename) {
        setEditingItem({ ...editingItem, image: data.filename });
      } else {
        alert(data.error || 'Upload failed');
      }
    } catch {
      alert('Upload failed');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleEditItem = (item: EquipmentItem) => {
    setEditingItem({ ...item });
    setIsCreating(false);
  };

  const handleCreateNew = () => {
    setEditingItem({
      id: `item-${Date.now()}`,
      stockNumber: '',
      title: '',
      category: 'roll-formers',
      condition: 'used',
      specifications: {},
      description: '',
      image: '',
    });
    setIsCreating(true);
  };

  const handleDeleteItem = (id: string) => {
    if (confirm('Are you sure you want to delete this item?')) {
      setEquipment(equipment.filter((item) => item.id !== id));
    }
  };

  const handleSaveItem = () => {
    if (!editingItem) return;

    if (isCreating) {
      setEquipment([...equipment, editingItem]);
    } else {
      setEquipment(equipment.map((item) => (item.id === editingItem.id ? editingItem : item)));
    }
    setEditingItem(null);
    setIsCreating(false);
  };

  const handleSpecChange = (key: string, value: string) => {
    if (!editingItem) return;
    setEditingItem({
      ...editingItem,
      specifications: { ...editingItem.specifications, [key]: value },
    });
  };

  const handleAddSpec = () => {
    if (!editingItem) return;
    const key = prompt('Specification name (e.g., "capacity", "width"):');
    if (key && key.trim()) {
      setEditingItem({
        ...editingItem,
        specifications: { ...editingItem.specifications, [key.trim()]: '' },
      });
    }
  };

  const handleRemoveSpec = (key: string) => {
    if (!editingItem) return;
    const { [key]: _, ...rest } = editingItem.specifications;
    setEditingItem({ ...editingItem, specifications: rest });
  };

  // Login form
  if (!isAuthenticated) {
    return (
      <div className="admin-page">
        <div className="admin-login">
          <h1>Admin Access</h1>
          <p>Enter the admin password to manage equipment inventory.</p>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              autoFocus
              disabled={loading}
            />
            <button type="submit" disabled={loading || !password}>
              {loading ? 'Logging in...' : 'Login'}
            </button>
            {error && <div className="error-message">{error}</div>}
          </form>
        </div>
      </div>
    );
  }

  // Equipment editor modal
  if (editingItem) {
    return (
      <div className="admin-page">
        <div className="admin-editor">
          <div className="editor-header">
            <h2>{isCreating ? 'Add New Equipment' : 'Edit Equipment'}</h2>
            <button className="close-btn" onClick={() => setEditingItem(null)}>
              &times;
            </button>
          </div>

          <div className="editor-form">
            <div className="form-group">
              <label>Stock Number</label>
              <input
                type="text"
                value={editingItem.stockNumber}
                onChange={(e) => setEditingItem({ ...editingItem, stockNumber: e.target.value })}
                placeholder="e.g., Stock #100"
              />
            </div>

            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={editingItem.title}
                onChange={(e) => setEditingItem({ ...editingItem, title: e.target.value })}
                placeholder="Equipment title"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Category</label>
                <select
                  value={editingItem.category}
                  onChange={(e) => setEditingItem({ ...editingItem, category: e.target.value })}
                >
                  <option value="roll-formers">Roll Formers</option>
                  <option value="uncoilers">Uncoilers</option>
                  <option value="presses">Presses</option>
                  <option value="complete-systems">Complete Systems</option>
                </select>
              </div>

              <div className="form-group">
                <label>Condition</label>
                <select
                  value={editingItem.condition}
                  onChange={(e) => setEditingItem({ ...editingItem, condition: e.target.value })}
                >
                  <option value="new">New</option>
                  <option value="demo">Demo/Like New</option>
                  <option value="used">Used</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editingItem.description}
                onChange={(e) => setEditingItem({ ...editingItem, description: e.target.value })}
                placeholder="Equipment description"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Image</label>
              <div className="image-upload">
                {editingItem.image && (
                  <div className="current-image">
                    <span>Current: {editingItem.image}</span>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleImageUpload}
                  disabled={uploadingImage}
                />
                {uploadingImage && <span className="uploading">Uploading...</span>}
              </div>
            </div>

            <div className="form-group specifications">
              <label>
                Specifications
                <button type="button" className="add-spec-btn" onClick={handleAddSpec}>
                  + Add
                </button>
              </label>
              <div className="spec-list">
                {Object.entries(editingItem.specifications).map(([key, value]) => (
                  <div key={key} className="spec-item">
                    <span className="spec-key">{key}:</span>
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => handleSpecChange(key, e.target.value)}
                    />
                    <button
                      type="button"
                      className="remove-spec-btn"
                      onClick={() => handleRemoveSpec(key)}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="editor-actions">
              <button className="cancel-btn" onClick={() => setEditingItem(null)}>
                Cancel
              </button>
              <button
                className="save-item-btn"
                onClick={handleSaveItem}
                disabled={!editingItem.stockNumber || !editingItem.title}
              >
                {isCreating ? 'Add Equipment' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Equipment list
  return (
    <div className="admin-page">
      <div className="admin-dashboard">
        <header className="admin-header">
          <h1>Equipment Inventory Manager</h1>
          <div className="header-actions">
            <span className={`save-status ${saveStatus}`}>
              {saveStatus === 'saving' && 'Saving...'}
              {saveStatus === 'saved' && 'Saved! Site will redeploy.'}
              {saveStatus === 'error' && 'Save failed'}
            </span>
            <button className="save-btn" onClick={handleSave} disabled={saveStatus === 'saving'}>
              Save & Deploy
            </button>
            <button className="logout-btn" onClick={handleLogout}>
              Logout
            </button>
          </div>
        </header>

        <div className="admin-toolbar">
          <button className="add-btn" onClick={handleCreateNew}>
            + Add Equipment
          </button>
          <span className="item-count">{equipment.length} items</span>
        </div>

        <div className="equipment-list">
          {equipment.map((item) => (
            <div key={item.id} className="equipment-row">
              <div className="equipment-info">
                <strong>{item.stockNumber}</strong>
                <span className="title">{item.title}</span>
                <span className={`badge ${item.condition}`}>{item.condition}</span>
                <span className="category">{item.category}</span>
              </div>
              <div className="equipment-actions">
                <button className="edit-btn" onClick={() => handleEditItem(item)}>
                  Edit
                </button>
                <button className="delete-btn" onClick={() => handleDeleteItem(item.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}

          {equipment.length === 0 && (
            <div className="empty-state">
              <p>No equipment items yet. Click "Add Equipment" to get started.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;
